import React, { useState } from "react";
import { Button, TextField, Tooltip, Typography } from "@mui/material";
import { GameOptionsPage } from "./GameOptionsPage";
import { Settings } from "../../Settings/Settings";
import { isValidConnectionHostname, isValidConnectionPort } from "../../Settings/SettingsUtils";
import { ArchipelagoConnectionStatus } from "./ArchipelagoConnectionStatus";
import { newArchipelagoConnection } from "../../Archipelago/Archipelago";
import { AP_GAME } from "../../Archipelago/Data";

export const ArchipelagoPage = (): React.ReactElement => {
  const [address, setAddress] = useState(Settings.ArchipelagoAddress);
  const [addressError, setAddressError] = useState(
    isValidConnectionHostname(Settings.ArchipelagoAddress).message ?? "",
  );
  const [port, setPort] = useState(Settings.ArchipelagoPort.toString());
  const [portError, setPortError] = useState(isValidConnectionPort(Settings.ArchipelagoPort).message ?? "");
  const [slotName, setSlotName] = useState(Settings.ArchipelagoSlotName);
  const [password, setPassword] = useState(Settings.ArchipelagoPassword);
  const [reconnectionDelay, setReconnectionDelay] = useState(Settings.ArchipelagoReconnectionDelay.toString());
  const [reconnectionDelayError, setReconnectionDelayError] = useState("");

  const isValidAddress = addressError === "";
  const isValidPort = portError === "";
  const isValidReconnectionDelay = reconnectionDelayError === "";

  function handleAddressChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value.trim();
    setAddress(newValue);
    const result = isValidConnectionHostname(newValue);
    if (!result.success) {
      setAddressError(result.message);
      return;
    }
    Settings.ArchipelagoAddress = newValue;
    setAddressError("");
  }

  function handlePortChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value.trim();
    setPort(newValue);
    const newPort = Number(newValue);
    const result = isValidConnectionPort(newPort);
    if (!result.success) {
      setPortError(result.message);
      return;
    }
    Settings.ArchipelagoPort = newPort;
    setPortError("");
  }

  function handleSlotNameChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value;
    setSlotName(newValue);
    Settings.ArchipelagoSlotName = newValue;
  }

  function handlePasswordChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value;
    setPassword(newValue);
    Settings.ArchipelagoPassword = newValue;
  }

  function handleReconnectionDelayChange(event: React.ChangeEvent<HTMLInputElement>): void {
    const newValue = event.target.value.trim();
    setReconnectionDelay(newValue);
    const newDelay = Number(newValue);
    if (!Number.isFinite(newDelay) || newDelay < 0) {
      setReconnectionDelayError("Invalid reconnection delay");
      return;
    }
    Settings.ArchipelagoReconnectionDelay = newDelay;
    setReconnectionDelayError("");
  }

  return (
    <GameOptionsPage title="Archipelago">
      <Typography>
        These settings control the Archipelago multiworld randomizer client. Receiving an item grants an augmentation,
        and earning an achievement sends a location check.
      </Typography>
      <Typography>
        This client currently connects as &quot;{AP_GAME}&quot; while Bitburner&apos;s own Archipelago world is in
        development.
      </Typography>
      <ArchipelagoConnectionStatus showIcon={false} />
      <Tooltip
        title={
          <Typography>
            The hostname of the Archipelago server.
            <br />
            If you use IPv6, you need to wrap it in square brackets. For example: [::1]
            <br />
            Default: localhost.
          </Typography>
        }
      >
        <div>
          <TextField
            error={!isValidAddress}
            InputProps={{
              startAdornment: <Typography style={{ minWidth: "200px" }}>Server address:&nbsp;</Typography>,
            }}
            value={address}
            onChange={handleAddressChange}
            placeholder="localhost"
            size={"medium"}
          />
          {addressError && <Typography color={Settings.theme.error}>{addressError}</Typography>}
        </div>
      </Tooltip>
      <Tooltip
        title={
          <Typography>
            The port of the Archipelago server.
            <br />
            The value must be in the range of [0, 65535]. Set it to 0 to disable the Archipelago client.
          </Typography>
        }
      >
        <div>
          <TextField
            error={!isValidPort}
            InputProps={{
              startAdornment: (
                <Typography color={isValidPort ? "success" : "error"} style={{ minWidth: "200px" }}>
                  Port:&nbsp;
                </Typography>
              ),
            }}
            value={port}
            onChange={handlePortChange}
            placeholder="38281"
            size={"medium"}
          />
          {portError && <Typography color={Settings.theme.error}>{portError}</Typography>}
        </div>
      </Tooltip>
      <Tooltip title={<Typography>The slot name you are connecting as, as defined in your player yaml.</Typography>}>
        <div>
          <TextField
            InputProps={{
              startAdornment: <Typography style={{ minWidth: "200px" }}>Slot name:&nbsp;</Typography>,
            }}
            value={slotName}
            onChange={handleSlotNameChange}
            placeholder="Player1"
            size={"medium"}
          />
        </div>
      </Tooltip>
      <Tooltip title={<Typography>The room password, if the server requires one. Leave empty otherwise.</Typography>}>
        <div>
          <TextField
            InputProps={{
              startAdornment: <Typography style={{ minWidth: "200px" }}>Password:&nbsp;</Typography>,
            }}
            value={password}
            onChange={handlePasswordChange}
            size={"medium"}
          />
        </div>
      </Tooltip>
      <Tooltip
        title={
          <Typography>
            When the connection is closed, Bitburner will automatically reconnect after this delay.
            <br />
            The value must be in seconds. Set it to 0 to disable the feature.
          </Typography>
        }
      >
        <div>
          <TextField
            error={!isValidReconnectionDelay}
            InputProps={{
              startAdornment: (
                <Typography color={isValidReconnectionDelay ? "success" : "error"} style={{ minWidth: "200px" }}>
                  Reconnection delay:&nbsp;
                </Typography>
              ),
            }}
            value={reconnectionDelay}
            onChange={handleReconnectionDelayChange}
            placeholder="5"
            size={"medium"}
          />
          {reconnectionDelayError && <Typography color={Settings.theme.error}>{reconnectionDelayError}</Typography>}
        </div>
      </Tooltip>
      <Button disabled={!isValidAddress || !isValidPort} onClick={newArchipelagoConnection}>
        Connect
      </Button>
    </GameOptionsPage>
  );
};
